import { useEffect, useRef } from "react";

import _ from "lodash";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import { useVero1Viewer } from "manager/components/vero1/vero1Viewer";
import Attachments from "shared/components/Attachments";
import AttachmentsRedesigned from "shared/components/Attachments/AttachmentsRedesigned";
import { VisibleAttachments } from "shared/components/Attachments/interfaces";
import Container from "shared/components/Container";
import CreditHistory from "shared/components/CreditHistory";
import CriminalHistory from "shared/components/CriminalHistory";
import FooterBar from "shared/components/FooterBar";
import { BackLink } from "shared/components/Links";
import PageTitle from "shared/components/PageTitle";
import Property from "shared/components/Property";
import Tabs from "shared/components/Tabs";
import Tile from "shared/components/Tile";
import { ApplicantWorkflow } from "shared/components/vega/ApplicantWorkflow";
import { LumenV2 } from "shared/components/vega/LumenV2";
import { Payments } from "shared/components/vega/Payments";
import ROUTES from "shared/config/routes";
import { useActiveUser } from "shared/hooks/api";
import { ApplicantType } from "shared/interfaces";
import { getFullnameData } from "shared/utils/applicant";
import { isRenterApp } from "shared/utils/auth";
import { isApplicationWithdrawable } from "shared/utils/deal-status";
import { useVeroFlag } from "shared/utils/featureFlags";
import {
  ocrResultsPropType,
  otherAttachmentsPropType,
  requiredAttachmentsPropType,
} from "shared/utils/propTypes";
import { transformRoute } from "shared/utils/routing";
import { VeroFlag } from "shared/utils/veroFlags";
import { useRepairAgreementAttachments } from "shared/hooks/api/applicationQueries";

import { mapSentilink } from "manager/hooks/application-summary/mappers";
import { useLegalAuditDocumentRedaction } from "manager/hooks/useLegalAuditDocumentRedaction";

import CreditAndBackgroundCheck from "./CreditAndBackgroundCheck";
import RentalHistory from "shared/components/RentalHistory";
import IdVerification from "./IdVerification";
import IncomeAndAssets from "./IncomeAndAssets";
import RenterProfile from "./RenterProfile";
import {
  DesktopApplicantDropdown,
  FooterBar as StyledFooterBar,
  FooterButton,
  LoadingSpinner,
  MobileApplicantDropdown,
} from "./styled";
import DefaultSummary from "./Summary";
import Verifications from "./Verifications";
import VorVerifications from "./VorVerifications";

const Application = ({
  addApplicationAttachments,
  ApplicantDropdown,
  applicantWorkflow,
  applicantWorkflowLoading,
  applicantWorkflowLookupFailed,
  application,
  attachmentActionInProgress,
  backLink,
  canAccessCountyCriminalRecords,
  canAccessSensitiveDealData,
  cancelApplicationAttachments,
  canViewOcrResults,
  canViewRestrictedAttachments,
  ConditionallyProceedInfoBanner,
  confirmApplicationAttachments,
  creditBackgroundIndicator,
  creditHistoryIndicator,
  rentalHistoryIndicator,
  criminalHistoryIndicator,
  deal,
  deleteApplicationAttachments,
  deleteDocumentById,
  deleteOcrResultById,
  failedDocuments,
  getDeal,
  history,
  incomeBalancesIndicator,
  isSensitiveDealDataPermissionLoaded,
  loading,
  lumenRun,
  match,
  ocrResults,
  openWithdraw,
  otherAttachments,
  payments,
  refetchOcrResults,
  requiredAttachments,
  showAttachmentsHeaders,
  showAttachmentsVisibilityAction,
  showConditionallyProceedInfoBanner,
  showDropdown,
  showOtherApplicants,
  showProperty,
  verificationsIndicator,
}) => {
  const { id } = match.params;
  const activeRoute = useLocation().pathname;
  const useAttachmentsRedesign = useVeroFlag(VeroFlag.useAttachmentsRedesign);
  const useAppSummaryTabRestructure = useVeroFlag(
    VeroFlag.useAppSummaryTabRestructure
  );
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const isRenter = isRenterApp();
  const { canViewInternal } = useVero1Viewer();
  const { isActiveUserLoading } = useActiveUser();
  const { repairAgreementAttachments } = useRepairAgreementAttachments();
  const agreementRepairTriggeredApplicationRef = useRef(null);

  const isV1AppReEntryEnabled = useVeroFlag(VeroFlag.useV1AppReEntry);

  const isWithdrawable = isRenter && isApplicationWithdrawable(application);

  const isV1ReEntry = isWithdrawable && isV1AppReEntryEnabled;

  useEffect(() => {
    if (application?.deal && showOtherApplicants) {
      getDeal(application?.deal);
    }
  }, [application?.deal]);

  const baseApplicationRoute = transformRoute(ROUTES.application, {
    id,
  });
  const renterProfileRoute = canAccessSensitiveDealData
    ? transformRoute(ROUTES.applicationRenterProfile, {
        id,
      })
    : baseApplicationRoute;
  const incomeAndAssetsRoute = transformRoute(ROUTES.applicationIncomeAssets, {
    id,
  });
  const creditAndBackgroundRoute = transformRoute(
    ROUTES.applicationCreditBackground,
    {
      id,
    }
  );
  const attachmentsRoute = transformRoute(ROUTES.applicationAttachments, {
    id,
  });
  const verificationsRoute = transformRoute(ROUTES.applicationVerifications, {
    id,
  });
  const idVerificationRoute = transformRoute(ROUTES.applicationIdVerification, {
    id,
  });
  const vorVerificationsRoute = transformRoute(
    ROUTES.applicationVorVerifications,
    {
      id,
    }
  );
  const workflowRoute = transformRoute(ROUTES.applicationWorkflow, {
    id,
  });
  const paymentsRoute = transformRoute(ROUTES.applicationPayments, {
    id,
  });
  const lumenRoute = transformRoute(ROUTES.applicationLumen, {
    id,
  });
  const creditHistoryRoute = transformRoute(ROUTES.applicationCreditHistory, {
    id,
  });

  useEffect(() => {
    if (
      agreementRepairTriggeredApplicationRef.current !== id &&
      !isRenter &&
      activeRoute === attachmentsRoute
    ) {
      agreementRepairTriggeredApplicationRef.current = id;
      repairAgreementAttachments(
        { applicationId: id },
        { onError: _.noop }
      );
    }
  }, [activeRoute, attachmentsRoute, id, isRenter, repairAgreementAttachments]);
  const rentalHistoryRoute = transformRoute(ROUTES.applicationRentalHistory, {
    id,
  });
  const criminalHistoryRoute = transformRoute(
    ROUTES.applicationCriminalHistory,
    {
      id,
    }
  );

  // NOTE: preserving the hash on redirect
  const redirectTo = {
    pathname: baseApplicationRoute,
    hash: (window.location.hash || "").substring(1),
  };

  const unitName = application?.unitName || "";

  const dealBackLink = {
    children: backLink.children.replace(":unitName", unitName),
    to: backLink.to.replace(":id", application?.deal),
  };
  const propertyName = application?.property?.name;
  const propertyAddress = application?.property?.address;
  const applicationSummary = application?.applicationSummary;
  const incomeAsset = application?.incomeAsset;
  const creditReport = application?.backgroundCreditCheck;
  const countyCriminalRecords = application?.countyCriminalRecords;
  const countyCriminalSearchStatus = application?.countyCriminalSearchStatus;

  const { fullNameOrNA, hasFullname } = getFullnameData(applicationSummary);
  const name = hasFullname ? fullNameOrNA : "Applicant";
  const pageTitle = `${name} Application`;

  const showRenterIdVerification = isRenter;
  const showManagerVerifications = !isRenter;
  const isNonVero1Deal = !_.isEmpty(deal) && !deal.vero1Active;
  const showLumen = !isRenter && (canViewInternal || isNonVero1Deal);
  const hasSSN = applicationSummary?.hasSsn;
  const reasonToStay =
    !hasSSN && applicationSummary?.reasonToStay
      ? `${isRenter ? "You" : name} did not provide an SSN/ITIN. Reason: ${
          applicationSummary?.reasonToStay
        }`
      : undefined;

  /* Tabs definition - start */
  const tabs = [];

  // Feature-flagged tab restructuring for Manager Portal
  if (!isRenter && useAppSummaryTabRestructure) {
    tabs.push({
      key: baseApplicationRoute,
      label: "Renter Profile",
    });

    tabs.push({
      key: verificationsRoute,
      label: <>ID Verification {verificationsIndicator}</>,
    });

    tabs.push({
      key: incomeAndAssetsRoute,
      label: <>Income {incomeBalancesIndicator}</>,
    });

    if (canAccessSensitiveDealData && !application?.isVeroGoActive) {
      if (!shouldRedactDocuments) {
        tabs.push({
          key: creditHistoryRoute,
          label: <>Credit History {creditBackgroundIndicator}</>,
        });
      }

      tabs.push({
        key: rentalHistoryRoute,
        label: <>Rental History</>,
      });

      if (!shouldRedactDocuments) {
        tabs.push({
          key: criminalHistoryRoute,
          label: <>Criminal History {creditBackgroundIndicator}</>,
        });
      }
    }

    tabs.push({
      key: paymentsRoute,
      label: <>Payments</>,
    });

    tabs.push({
      key: attachmentsRoute,
      label: "Attachments",
    });

    tabs.push({
      key: workflowRoute,
      label: <>Workflow</>,
    });

    if (showLumen) {
      tabs.push({
        key: lumenRoute,
        label: useDealsListUiRedesign ? <>Checklist</> : <>Lumen</>,
      });
    }
  } else {
    // legacy tab logic when feature flag is OFF
    tabs.push(
      {
        key: baseApplicationRoute,
        label: isRenter ? "My Profile" : "Renter Profile",
      },
      {
        key: incomeAndAssetsRoute,
        label: <>Income & Balances {incomeBalancesIndicator}</>,
      }
    );

    if (canAccessSensitiveDealData && !shouldRedactDocuments) {
      tabs.push({
        key: creditAndBackgroundRoute,
        label: <>Credit & Background {creditBackgroundIndicator}</>,
      });
    }

    tabs.push({
      key: attachmentsRoute,
      label: "Attachments",
    });

    if (showRenterIdVerification) {
      tabs.push({
        key: idVerificationRoute,
        label: "ID Verification",
      });
    }

    if (showManagerVerifications) {
      tabs.push({
        key: verificationsRoute,
        label: <>Identity Verifications {verificationsIndicator}</>,
      });
      tabs.push({
        key: vorVerificationsRoute,
        label: <>Residency Verifications</>,
      });
    }

    tabs.push({
      key: workflowRoute,
      label: <>Workflow</>,
    });

    tabs.push({
      key: paymentsRoute,
      label: <>Payments</>,
    });

    if (showLumen) {
      tabs.push({
        key: lumenRoute,
        label: useDealsListUiRedesign ? <>Checklist</> : <>Lumen</>,
      });
    }
  }

  /* Tabs definition - end */

  const showLoader =
    (loading && !application) ||
    !isSensitiveDealDataPermissionLoaded ||
    (!isRenter && isActiveUserLoading);

  const applicantType = application?.applicantType;
  let applicantTypeLabel;
  if (applicantType === ApplicantType.APPLICANT) {
    applicantTypeLabel = "Applicant";
  } else if (applicantType === ApplicantType.GUARANTOR) {
    applicantTypeLabel = "Guarantor";
  } else if (applicantType === ApplicantType.OCCUPANT) {
    applicantTypeLabel = "Occupant";
  }

  const party = _.upperCase(application?.party);

  return (
    <>
      {showLoader ? (
        <LoadingSpinner />
      ) : (
        <>
          <Helmet>
            <title>{pageTitle}</title>
          </Helmet>
          {isV1ReEntry && (
            <style>{`
              .edit-app-btn { min-width: 160px; }
              @media (min-width: 768px) { .edit-app-btn { min-width: 220px; } }
              @media (min-width: 1200px) { .edit-app-btn { min-width: 280px; } }
            `}</style>
          )}
          {isV1ReEntry && (
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                background: "#fff",
                borderBottom: "1px solid #EEEEEE",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.917 49c0-.966.783-1.75 1.75-1.75h46.666a1.75 1.75 0 110 3.5H4.667A1.75 1.75 0 012.917 49z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.667 32.083a.583.583 0 00-.583.584v14a.583.583 0 00.583.583h4.667a.583.583 0 00.583-.583v-14a.583.583 0 00-.583-.584h-4.667zM7.584 32.667a4.083 4.083 0 014.083-4.084h4.667a4.083 4.083 0 014.083 4.084v14a4.083 4.083 0 01-4.083 4.083h-4.667a4.083 4.083 0 01-4.083-4.083v-14z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.25 39.667a1.75 1.75 0 011.75-1.75h1.167a1.75 1.75 0 110 3.5H14a1.75 1.75 0 01-1.75-1.75z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21 6.417a.583.583 0 00-.583.583v39.667a.583.583 0 00.583.583h23.333a.583.583 0 00.584-.583V7a.583.583 0 00-.584-.583H21zM16.917 7A4.083 4.083 0 0121 2.917h23.333A4.083 4.083 0 0148.417 7v39.667a4.083 4.083 0 01-4.084 4.083H21a4.083 4.083 0 01-4.083-4.083V7z"
                    fill="currentColor"
                  />
                  <path
                    d="M30.334 11.667h-4.667v4.666h4.667v-4.666zM39.667 11.667H35v4.666h4.667v-4.666zM30.334 19.833h-4.667v4.667h4.667v-4.667zM39.667 19.833H35v4.667h4.667v-4.667zM39.667 28H35v4.667h4.667V28zM39.667 36.167H35v4.666h4.667v-4.666z"
                    fill="currentColor"
                  />
                </svg>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>
                    {[propertyName, unitName].filter(Boolean).join(", ") ||
                      `Application for ${
                        application?.companyName || "company"
                      }`}
                  </div>
                  {propertyAddress && (
                    <div
                      style={{ fontSize: 13, fontWeight: 300, color: "#666" }}
                    >
                      {propertyAddress}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", flexShrink: 0 }}>
                <button
                  className="edit-app-btn"
                  onClick={() => {
                    openWithdraw(
                      application.id,
                      propertyName,
                      application?.useV1ExpressionEngine
                    );
                  }}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    height: 40,
                    padding: "0 20px",
                    backgroundColor: "#A374F5",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    lineHeight: 1.3,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#8052D0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#A374F5";
                  }}
                >
                  Edit Application
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          <Container noPaddingBottom>
            <BackLink.Container>
              <BackLink {...dealBackLink} />
            </BackLink.Container>
            {showConditionallyProceedInfoBanner && (
              <Container noPaddingTop noPaddingRight noPaddingLeft>
                <ConditionallyProceedInfoBanner />
              </Container>
            )}
            <PageTitle.Container>
              <PageTitle>{pageTitle}</PageTitle>
              <PageTitle.Actions mobileRightAlign rightAlign={showProperty}>
                {showProperty && !isV1ReEntry && (
                  <Property
                    horizontal
                    name={propertyName}
                    address={propertyAddress}
                    unit={unitName}
                    fallbackContent={`Application for ${
                      application?.companyName || "company"
                    }`}
                  />
                )}
                {showDropdown && (
                  <>
                    <DesktopApplicantDropdown>
                      <ApplicantDropdown
                        canAccessSensitiveDealData={canAccessSensitiveDealData}
                      />
                    </DesktopApplicantDropdown>
                    <MobileApplicantDropdown>
                      <ApplicantDropdown
                        canAccessSensitiveDealData={canAccessSensitiveDealData}
                      />
                    </MobileApplicantDropdown>
                  </>
                )}
              </PageTitle.Actions>
            </PageTitle.Container>
          </Container>
          <Container noMobilePaddingX noPaddingTop>
            <Tile noBorderBottom>
              <Tabs
                activeKey={activeRoute}
                tabs={tabs}
                onChange={(newRoute) => history.push(newRoute)}
              />
            </Tile>
            <Switch>
              <Route
                exact
                path={baseApplicationRoute}
                render={() => (
                  <RenterProfile
                    type={party || applicantTypeLabel}
                    name={name}
                    applicationSummary={applicationSummary}
                  />
                )}
              />
              <Route exact path={renterProfileRoute}>
                <Redirect to={redirectTo} />
              </Route>
              <Route
                exact
                path={incomeAndAssetsRoute}
                render={() => (
                  <IncomeAndAssets
                    summary={incomeAsset}
                    applicationSummary={applicationSummary}
                    ocrResults={ocrResults}
                    canViewOcrResults={canViewOcrResults}
                  />
                )}
              />
              {canAccessSensitiveDealData && !shouldRedactDocuments && (
                <Route
                  exact
                  path={creditAndBackgroundRoute}
                  render={() =>
                    !isRenter && useAppSummaryTabRestructure ? (
                      <CreditHistory
                        creditScoreSummary={creditReport?.creditScoreSummary}
                        noSSNreasonToStay={reasonToStay}
                        deal={deal}
                        application={application}
                      />
                    ) : (
                      <CreditAndBackgroundCheck
                        hideCriminalRecords={application?.hideCriminalRecords}
                        isEvictions={!application?.isInNoEvictionState}
                        backgroundCreditCheck={creditReport}
                        countyCriminalRecords={countyCriminalRecords}
                        noSSNreasonToStay={reasonToStay}
                        canAccessCountyCriminalRecords={
                          canAccessCountyCriminalRecords
                        }
                        countyCriminalSearchStatus={countyCriminalSearchStatus}
                        hasCountyCriminalSearch={
                          application?.hasCountyCriminalSearch
                        }
                        hasTuCountyCriminalSearch={
                          application?.hasTuCountyCriminalSearch
                        }
                        deal={deal}
                        application={application}
                      />
                    )
                  }
                />
              )}

              {/* Criminal History route */}
              {!isRenter &&
                useAppSummaryTabRestructure &&
                canAccessSensitiveDealData &&
                !application?.isVeroGoActive &&
                !shouldRedactDocuments && (
                  <Route
                    exact
                    path={criminalHistoryRoute}
                    render={() => (
                      <CriminalHistory
                        creditScoreSummary={creditReport?.creditScoreSummary}
                        criminalRecords={creditReport?.criminalRecords}
                        hideCriminalRecords={application?.hideCriminalRecords}
                        canAccessCountyCriminalRecords={
                          canAccessCountyCriminalRecords
                        }
                        countyCriminalRecords={countyCriminalRecords}
                        countyCriminalSearchStatus={countyCriminalSearchStatus}
                        hasCountyCriminalSearch={
                          application?.hasCountyCriminalSearch
                        }
                        hasTuCountyCriminalSearch={
                          application?.hasTuCountyCriminalSearch
                        }
                        deal={deal}
                        application={application}
                      />
                    )}
                  />
                )}

              {/* Credit History route */}
              {!isRenter &&
                useAppSummaryTabRestructure &&
                canAccessSensitiveDealData &&
                !application?.isVeroGoActive &&
                !shouldRedactDocuments && (
                  <Route
                    exact
                    path={creditHistoryRoute}
                    render={() => (
                      <CreditHistory
                        creditScoreSummary={creditReport?.creditScoreSummary}
                        noSSNreasonToStay={reasonToStay}
                        deal={deal}
                        application={application}
                      />
                    )}
                  />
                )}

              {/* Rental History route */}
              {!isRenter &&
                useAppSummaryTabRestructure &&
                canAccessSensitiveDealData &&
                !application?.isVeroGoActive && (
                  <Route
                    exact
                    path={rentalHistoryRoute}
                    render={() => (
                      <RentalHistory
                        creditScoreSummary={creditReport?.creditScoreSummary}
                        evictions={creditReport?.evictionRecords}
                        isEvictions={!application?.isInNoEvictionState}
                        deal={deal}
                        application={application}
                        backgroundCreditCheck={creditReport}
                        verifications={application?.verifications}
                      />
                    )}
                  />
                )}

              <Route
                exact
                path={attachmentsRoute}
                render={() =>
                  useAttachmentsRedesign ? (
                    <AttachmentsRedesigned />
                  ) : (
                    <Attachments
                      ocrResults={ocrResults}
                      deleteOcrResultById={deleteOcrResultById}
                      deleteDocumentById={deleteDocumentById}
                      refetchOcrResults={refetchOcrResults}
                      canViewOcrResults={canViewOcrResults}
                      canViewRestrictedAttachments={
                        canViewRestrictedAttachments
                      }
                      showAttachmentsHeaders={showAttachmentsHeaders}
                      showAttachmentsVisibilityAction={
                        showAttachmentsVisibilityAction
                      }
                      requiredAttachments={requiredAttachments}
                      otherAttachments={otherAttachments}
                      uploadAttachment={(params) =>
                        addApplicationAttachments({
                          applicationId: id,
                          ...params,
                        })
                      }
                      deleteAttachment={(attachmentId) =>
                        deleteApplicationAttachments({
                          applicationId: id,
                          attachmentId,
                        })
                      }
                      confirmApplicationAttachments={(
                        attachmentsChangedVisiblility,
                        onSuccess
                      ) =>
                        confirmApplicationAttachments(
                          {
                            applicationId: id,
                            attachmentsChangedVisiblility,
                          },
                          { onSuccess }
                        )
                      }
                      cancelApplicationAttachments={(onSuccess) =>
                        cancelApplicationAttachments(
                          {
                            applicationId: id,
                          },
                          { onSuccess }
                        )
                      }
                      loading={attachmentActionInProgress || loading}
                      withOtherAttachmentsSection
                      inEditMode={!isRenter}
                      dealDecisionIsMade={application?.dealDecisionIsMade}
                      failedDocuments={failedDocuments}
                      documents={application?.documents}
                      applicantDocuments={_.get(
                        application,
                        "snapshot.applicantDocuments"
                      )}
                    />
                  )
                }
              />

              {showRenterIdVerification && (
                <Route
                  exact
                  path={idVerificationRoute}
                  render={() => (
                    <IdVerification
                      verification={application?.idVerification}
                      personaStatus={
                        application?.applicationSummary?.personaStatus
                      }
                    />
                  )}
                />
              )}
              {showManagerVerifications && (
                <Route
                  exact
                  path={verificationsRoute}
                  render={() => (
                    <Verifications
                      verifications={application?.verifications}
                      idVerifications={application?.idVerifications}
                      personaInquiryStatus={application?.personaInquiryStatus}
                      personaStatus={
                        application?.applicationSummary?.personaStatus
                      }
                      sentilink={mapSentilink(application)}
                      applicantWorkflow={applicantWorkflow}
                      applicantWorkflowLoading={applicantWorkflowLoading}
                      applicantWorkflowLookupFailed={
                        applicantWorkflowLookupFailed
                      }
                    />
                  )}
                />
              )}
              {showManagerVerifications && (
                <Route
                  exact
                  path={vorVerificationsRoute}
                  render={() => (
                    <VorVerifications
                      verifications={application?.verifications}
                    />
                  )}
                />
              )}
              <Route
                exact
                path={workflowRoute}
                render={() => (
                  <ApplicantWorkflow applicantWorkflow={applicantWorkflow} />
                )}
              />
              <Route
                exact
                path={paymentsRoute}
                render={() => <Payments payments={payments} />}
              />
              {showLumen && (
                <Route
                  exact
                  path={lumenRoute}
                  render={() => (
                    <LumenV2
                      lumenRun={lumenRun}
                      applicantId={application?.applicantVrn}
                      detailed={canAccessSensitiveDealData}
                    />
                  )}
                />
              )}

              <Redirect to={redirectTo} />
            </Switch>
          </Container>
          <FooterBar.Spacer />
          {isWithdrawable && !isV1AppReEntryEnabled && (
            <StyledFooterBar>
              <FooterButton
                type="secondary"
                data-testid="editApplicationButton"
                onClick={() => openWithdraw(application.id, propertyName)}
              >
                Edit Application
              </FooterButton>
            </StyledFooterBar>
          )}
        </>
      )}
    </>
  );
};

Application.propTypes = {
  editIncomeAndAssets: PropTypes.func,
  removeApplicant: PropTypes.func,
  backLink: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  application: PropTypes.object,
  applicantTypes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  getDeal: PropTypes.func,
  showOtherApplicants: PropTypes.bool,
  showDropdown: PropTypes.bool,
  showProperty: PropTypes.bool,
  showDateStamp: PropTypes.bool,
  openWithdraw: PropTypes.func,
  requiredAttachments: requiredAttachmentsPropType,
  requiredAttachmentsRaw: requiredAttachmentsPropType,
  otherAttachments: otherAttachmentsPropType,
  otherAttachmentsRaw: otherAttachmentsPropType,
  ocrResults: ocrResultsPropType,
  deleteOcrResultById: PropTypes.func,
  refetchOcrResults: PropTypes.func,
  OtherApplicantsList: PropTypes.elementType.isRequired,
  ApplicantDropdown: PropTypes.elementType,
  ConditionallyProceedInfoBanner: PropTypes.elementType,
  showConditionallyProceedInfoBanner: PropTypes.bool,
  title: PropTypes.string.isRequired,
  addApplicationAttachments: PropTypes.func,
  deleteApplicationAttachments: PropTypes.func,
  confirmApplicationAttachments: PropTypes.func,
  cancelApplicationAttachments: PropTypes.func,
  attachmentActionInProgress: PropTypes.bool,
  canAccessSensitiveDealData: PropTypes.bool,
  canViewOcrResults: PropTypes.bool,
  isSensitiveDealDataPermissionLoaded: PropTypes.bool,
  canAccessCountyCriminalRecords: PropTypes.bool,
  Summary: PropTypes.elementType,
  showAttachmentsHeaders: PropTypes.bool,
  showAttachmentsVisibilityAction: PropTypes.bool,
  visibleAttachments: PropTypes.oneOf([
    VisibleAttachments.ALL,
    VisibleAttachments.PUBLIC,
  ]),
  creditBackgroundIndicator: PropTypes.elementType,
  incomeBalancesIndicator: PropTypes.elementType,
  verificationsIndicator: PropTypes.elementType,
  // V2-specific indicators
  creditHistoryIndicator: PropTypes.elementType,
  rentalHistoryIndicator: PropTypes.elementType,
  criminalHistoryIndicator: PropTypes.elementType,
  applicantWorkflow: PropTypes.object,
  applicantWorkflowLoading: PropTypes.bool,
  applicantWorkflowLookupFailed: PropTypes.bool,
  payments: PropTypes.array,
  lumenRun: PropTypes.object,
};

Application.defaultProps = {
  application: undefined,
  showOtherApplicants: false,
  showProperty: false,
  showDateStamp: false,
  getDeal: undefined,
  editIncomeAndAssets: undefined,
  removeApplicant: undefined,
  showDropdown: true,
  ApplicantDropdown: undefined,
  ConditionallyProceedInfoBanner: undefined,
  showConditionallyProceedInfoBanner: false,
  openWithdraw: undefined,
  requiredAttachments: [],
  requiredAttachmentsRaw: [],
  otherAttachments: [],
  otherAttachmentsRaw: [],
  addApplicationAttachments: undefined,
  deleteApplicationAttachments: undefined,
  confirmApplicationAttachments: undefined,
  cancelApplicationAttachments: undefined,
  attachmentActionInProgress: false,
  canAccessSensitiveDealData: false,
  isSensitiveDealDataPermissionLoaded: false,
  canAccessCountyCriminalRecords: false,
  canViewOcrResults: false,
  Summary: DefaultSummary,
  showAttachmentsHeaders: false,
  showAttachmentsVisibilityAction: false,
  applicantWorkflow: {},
  applicantWorkflowLoading: false,
  applicantWorkflowLookupFailed: false,
  payments: [],
  lumenRun: {},
};

export default Application;
